'use client'

import CharacterDetailModal from '@/app/_components/CharacterDetail/CharacterDetailModal'
import ChevronBackward from '@/app/_components/Icon/ChevronBackward'
import ChevronForward from '@/app/_components/Icon/ChevronForward'
import { cn } from '@/app/_primitives/utils/cn'
import { CharacterDetails } from '@/backend/models/Character'
import Image from 'next/image'
import { useEffect, useMemo, useRef, useState } from 'react'

export function Library(props: { character: CharacterDetails }) {
  const { character } = props
  const [intersectRatios, setIntersectRatios] = useState<number[]>(() =>
    Array.from({
      length: character.extraImages.length,
    }).map(() => 0),
  )

  const [nextFirstIdx, nextLastIdx] = useMemo(() => {
    let firstIntersectIdx = 0
    let lastIntersectIdx = 0

    intersectRatios.forEach((ratio, idx) => {
      if (ratio > 0.5) {
        lastIntersectIdx = idx
      }

      if (ratio <= 0.5) {
        firstIntersectIdx = idx
      }
    })

    intersectRatios.toReversed().forEach((ratio, idx) => {
      if (ratio > 0.5) {
        firstIntersectIdx = intersectRatios.length - 1 - idx
      }
    })

    return [firstIntersectIdx - 1, lastIntersectIdx + 1]
  }, [intersectRatios])

  const scrollToCard = (idx: number) => {
    const card = document.querySelector<HTMLButtonElement>(
      `.library-card-${idx}`,
    )
    if (!card) return

    card.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  const onNext = () => {
    if (nextLastIdx >= character.extraImages.length) return

    scrollToCard(nextLastIdx)
  }

  const onPrev = () => {
    if (nextFirstIdx < 0) return

    scrollToCard(nextFirstIdx)
  }

  return (
    <div className="container relative">
      <button
        onClick={onPrev}
        type="button"
        className={cn(
          'absolute left-0 top-1/2 -translate-y-1/2 outline-none xl:-left-44',
        )}>
        <ChevronBackward width={24} height={24} fill="#fff" />
      </button>
      <button
        onClick={onNext}
        type="button"
        className={cn(
          'absolute right-0 top-1/2 -translate-y-1/2 outline-none xl:-right-44',
        )}>
        <ChevronForward width={24} height={24} fill="#fff" />
      </button>
      <div
        className={cn(
          'library-container no-scrollbar overflow-auto',
          'flex flex-row items-center gap-16 xl:gap-24',
        )}>
        {character.extraImages.map((extra, idx) => {
          return (
            <LibraryItem
              idx={idx}
              name={extra.name}
              imageURL={extra.url}
              key={extra.id}
              backgroundColor={'#FFFFFF'}
              onIntersectChange={(ratio) => {
                setIntersectRatios((prev) => {
                  const newRatios = [...prev]
                  newRatios[idx] = ratio

                  return newRatios
                })
              }}
            />
          )
        })}
      </div>
    </div>
  )
}

function LibraryItem(props: {
  idx: number
  name: string
  imageURL: string
  backgroundColor: string

  onIntersectChange: (ratio: number) => void
}) {
  const [open, setOpen] = useState(false)
  const divRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!divRef.current) return

    const div = divRef.current
    const intersectionObserver = new IntersectionObserver((entries) => {
      const entry = entries[0]
      if (!entry) return

      props.onIntersectChange(
        entry.isIntersecting ? entry.intersectionRatio : 0,
      )
    })

    intersectionObserver.observe(div)

    return () => intersectionObserver.disconnect()
  }, [props])

  return (
    <div className={'mt-10 min-h-170'}>
      <CharacterDetailModal
        characterInfo={{
          id: props.idx.toString(),
          name: props.name,
          imageURL: props.imageURL,
          user: {
            displayName: '',
          },
        }}
        isOpen={open}
        setIsOpen={setOpen}
      />
      <button
        onClick={() => setOpen(true)}
        type="button"
        ref={divRef}
        style={{
          backgroundColor: props.backgroundColor,
        }}
        className={cn(
          'outline-none',
          `library-card-${props.idx}`,
          'flex flex-row items-center justify-center',
          'relative min-h-170 w-auto min-w-280 overflow-hidden rounded-27',
          'hover:-mt-10 hover:shadow-xl',
        )}>
        <Image
          src={props.imageURL}
          alt={props.name}
          width={170}
          height={170}
          style={{
            width: 'auto',
            height: '170px',
            minWidth: '280px',
            aspectRatio: 'auto',
            objectFit: 'cover',
          }}
        />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(0, 0, 0, 0) 49.85%, rgba(0, 0, 0, 0.6) 100%)',
          }}
        />
        <p className="c-h7 absolute bottom-10 left-18 text-white">
          {props.name}
        </p>
      </button>
    </div>
  )
}
